import { create } from 'zustand';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReactNode } from 'react';

export enum BottomSheetSize {
  SM = 'sm',
  MD = 'md',
  LG = 'lg'
}

interface BottomSheetState {
  isOpen: boolean;
  title: string;
  component: ReactNode | null;
  size?: BottomSheetSize;
  closeOnBackdropClick?: boolean;
  open: (title: string, component: ReactNode, size?: BottomSheetSize, closeOnBackdropClick?: boolean) => void;
  close: () => void;
}

export const useBottomSheetStore = create<BottomSheetState>((set) => ({
  isOpen: false,
  title: '',
  component: null,
  size: BottomSheetSize.MD,
  closeOnBackdropClick: true,
  open: (title: string, component: ReactNode, size?: BottomSheetSize, closeOnBackdropClick?: boolean) => set({ isOpen: true, title, component, size, closeOnBackdropClick }),
  close: () => set({ isOpen: false, title: '', component: null, closeOnBackdropClick: true })
}));

export const useBottomSheet = () => {
  const { open, close, isOpen, title, component, size, closeOnBackdropClick } = useBottomSheetStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // Update URL when bottom sheet state changes
  useEffect(() => {
    if (isOpen) {
      // If bottom sheet is open, add a parameter to indicate it's open
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('bottomSheet', 'open');
      setSearchParams(newSearchParams, { replace: true });
    } else {
      // If drawer is closed, remove the parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('bottomSheet');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [isOpen, searchParams, setSearchParams]);

  const openBottomSheet = (props: { title: string, component: ReactNode, size?: BottomSheetSize, closeOnBackdropClick?: boolean }) => {
    open(props.title, props.component, props.size, props.closeOnBackdropClick);
  };

  const closeBottomSheet = async (): Promise<void> => {
    return new Promise<void>((resolve) => {
      close();

      setTimeout(() => {
        resolve();
      }, 250);
    });

  };

  return {
    openBottomSheet,
    closeBottomSheet,
    isBottomSheetOpen: isOpen,
    bottomSheetTitle: title,
    bottomSheetComponent: component,
    bottomSheetSize: size,
    closeOnBackdropClick
  };
}; 